import { compact } from '@leland-dev/leland-ui-library';
import {
  type GetStaticPaths,
  type GetStaticProps,
  type GetStaticPropsResult,
} from 'next';
import { useRouter } from 'next/router';
import React, { type FC, useEffect } from 'react';

import { useLoginModal } from '../../components/LoginModal';
import ClassPage, {
  ClassPageRefetchQueries,
  type ClassPageStaticProps,
  getClassPageStaticData,
  useClassPageRehydratedData,
} from '../../components/classes/ClassPage';
import { useAuth } from '../../context/AuthContext';
import { useLex } from '../../context/LexContext';
import { initializeApollo } from '../../lib/apolloClient';
import { getCoachingSessionMeetingUrl } from '../../utils/coaching-session';

import {
  PublishedEventPageGetStaticClassPathsDocument,
  type PublishedEventPageGetStaticClassPathsQuery,
  type PublishedEventPageGetStaticClassPathsQueryVariables,
} from './__generated-gql-types__/[eventSlug].generated';

const PublishedEventPage: FC<ClassPageStaticProps> = ({
  staticClassData,
  pageContentProps,
}) => {
  const router = useRouter();
  const { isLexEnabled, isLexLoading } = useLex();
  const { currentUser, isLoadingUser } = useAuth();

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();

  const eventSlug = router.query.eventSlug as string;
  const sessionIdToOpen = router.query.openSid as Optional<string>;

  const { data: classData, error: classDataError } =
    useClassPageRehydratedData(eventSlug);

  const isLoggedInAsApplicant = !!currentUser?.applicant;
  useEffect(() => {
    if (!isLoadingUser && sessionIdToOpen && classData) {
      if (!isLoggedInAsApplicant) {
        setLoginModalOpen(true);
      }
      const sessionToOpen =
        classData.groupClassBySlug.userEnrolledInSessionsV2?.find(
          (s) => s.id === sessionIdToOpen,
        );
      if (sessionToOpen?.meetingLink) {
        void router.push(getCoachingSessionMeetingUrl(sessionToOpen.id));
      }
    }
    if (classData && !classData.groupClassBySlug.isFreeEvent) {
      void router.push(`/class/${eventSlug}`);
    }
  }, [
    isLoadingUser,
    sessionIdToOpen,
    classData,
    isLoggedInAsApplicant,
    router,
    setLoginModalOpen,
    eventSlug,
    isLexEnabled,
    isLexLoading,
  ]);

  return (
    <>
      <ClassPage
        error={!!classDataError}
        classData={classData?.groupClassBySlug ?? staticClassData}
        pageContentProps={pageContentProps}
        reload={router.reload}
        trackingPrefix="PublishedEventPage"
      />
      <LoginModal
        hideCloseButton
        onApplicantLogin={() => setLoginModalOpen(false)}
        refetchQueries={ClassPageRefetchQueries}
      />
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PathParams = {
  eventSlug?: string;
};
export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<
    Possible<PublishedEventPageGetStaticClassPathsQuery>,
    PublishedEventPageGetStaticClassPathsQueryVariables
  >({ query: PublishedEventPageGetStaticClassPathsDocument });

  return {
    paths: (data?.searchClasses.results ?? [])
      .map(({ slug }) => (slug ? { params: { eventSlug: slug } } : null))
      .filter(compact),
    fallback: 'blocking',
  };
};

const REVALIDATE_TIME_IN_SECONDS = 3600; // 1 hour
const NOT_FOUND_PROPS: GetStaticPropsResult<ClassPageStaticProps> = {
  notFound: true,
  revalidate: REVALIDATE_TIME_IN_SECONDS,
};

export const getStaticProps: GetStaticProps<
  ClassPageStaticProps,
  PathParams
> = async ({ params: { eventSlug } = {} }) => {
  if (!eventSlug) {
    return NOT_FOUND_PROPS;
  }

  const apolloClient = initializeApollo();
  const { props: propsToUse, errors } = await getClassPageStaticData(
    apolloClient,
    eventSlug,
  );

  if (!propsToUse?.staticClassData) {
    if (!errors || errors?.some((e) => e.extensions?.statusCode === 404)) {
      return NOT_FOUND_PROPS;
    }

    // If not 404, throw to explicitly treat as 5xx
    console.warn(errors);
    throw new Error('Failed to load event data');
  }

  return {
    props: propsToUse,
    revalidate: REVALIDATE_TIME_IN_SECONDS,
  };
};

export default PublishedEventPage;
